import {
  LeadRecord,
  OpportunityRecord,
  LoanOfficer,
  FilterState,
  FunnelMetrics,
  LoanOfficerMetrics,
  FunnelStep,
} from '../types/throughput'
import { isWithinInterval, parseISO } from 'date-fns'

export const filterData = (
  leads: LeadRecord[],
  opportunities: OpportunityRecord[],
  officers: LoanOfficer[],
  filters: FilterState,
) => {
  // 1. Filter Leads by Date Range (Cohort)
  let filteredLeads = leads.filter((lead) => {
    const created = parseISO(lead.createdAt)
    return isWithinInterval(created, {
      start: parseISO(filters.dateRange.start),
      end: parseISO(filters.dateRange.end),
    })
  })

  // 2. Filter by Lead Source
  if (filters.leadSources.length > 0) {
    filteredLeads = filteredLeads.filter((lead) =>
      filters.leadSources.includes(lead.source),
    )
  }

  // 3. Filter by Scope (Officers)
  let scopedOfficerIds: string[] = []

  if (filters.scopeType === 'All') {
    scopedOfficerIds = officers.map((o) => o.id)
  } else if (filters.scopeType === 'SVP' && filters.selectedScopeId) {
    scopedOfficerIds = officers
      .filter(
        (o) =>
          o.svpId === filters.selectedScopeId ||
          o.id === filters.selectedScopeId,
      )
      .map((o) => o.id)
  } else if (filters.scopeType === 'Team Lead' && filters.selectedScopeId) {
    scopedOfficerIds = officers
      .filter(
        (o) =>
          o.teamLeadId === filters.selectedScopeId ||
          o.id === filters.selectedScopeId,
      )
      .map((o) => o.id)
  } else if (filters.scopeType === 'Loan Officer' && filters.selectedScopeId) {
    scopedOfficerIds = [filters.selectedScopeId]
  }

  // Filter out inactive if needed
  if (!filters.includeInactive) {
    const activeIds = new Set(
      officers.filter((o) => o.isActive).map((o) => o.id),
    )
    scopedOfficerIds = scopedOfficerIds.filter((id) => activeIds.has(id))
  }

  filteredLeads = filteredLeads.filter((lead) =>
    scopedOfficerIds.includes(lead.ownerId),
  )

  // Get corresponding opportunities
  const leadIds = new Set(filteredLeads.map((l) => l.id))
  const filteredOpportunities = opportunities.filter((opp) =>
    leadIds.has(opp.leadId),
  )

  return { filteredLeads, filteredOpportunities, scopedOfficerIds }
}

export const calculateFunnelMetrics = (
  leads: LeadRecord[],
  opportunities: OpportunityRecord[],
): FunnelMetrics[] => {
  const counts = {
    Created: leads.length,
    Converted: opportunities.length,
    'App Out': opportunities.filter((o) => o.appOutDate).length,
    'App Completed': opportunities.filter((o) => o.appCompletedDate).length,
    'Disclosures Sent': opportunities.filter((o) => o.disclosuresSentDate)
      .length,
    'Disclosures Received': opportunities.filter(
      (o) => o.disclosuresReceivedDate,
    ).length,
    'Submitted to UW': opportunities.filter((o) => o.submittedToUWDate).length,
    'Approved with Conditions': opportunities.filter(
      (o) => o.approvedWithConditionsDate,
    ).length,
    'Closing Scheduled': opportunities.filter((o) => o.closingScheduledDate)
      .length,
    Funded: opportunities.filter((o) => o.fundedDate).length,
  }

  const steps: FunnelStep[] = [
    'Created',
    'Converted',
    'App Out',
    'App Completed',
    'Disclosures Sent',
    'Disclosures Received',
    'Submitted to UW',
    'Approved with Conditions',
    'Closing Scheduled',
    'Funded',
  ]

  return steps.map((step, index) => {
    const count = counts[step]
    const prevCount = index === 0 ? count : counts[steps[index - 1]]
    const createdCount = counts['Created']

    return {
      step,
      count,
      conversionRate: prevCount > 0 ? (count / prevCount) * 100 : 0,
      overallConversionRate:
        createdCount > 0 ? (count / createdCount) * 100 : 0,
    }
  })
}

export const calculateOfficerMetrics = (
  officers: LoanOfficer[],
  leads: LeadRecord[],
  opportunities: OpportunityRecord[],
): LoanOfficerMetrics[] => {
  return officers.map((officer) => {
    const officerLeads = leads.filter((l) => l.ownerId === officer.id)
    const officerOpps = opportunities.filter((o) => o.ownerId === officer.id)

    const counts = {
      Created: officerLeads.length,
      Converted: officerOpps.length,
      'App Out': officerOpps.filter((o) => o.appOutDate).length,
      'App Completed': officerOpps.filter((o) => o.appCompletedDate).length,
      'Disclosures Sent': officerOpps.filter((o) => o.disclosuresSentDate)
        .length,
      'Disclosures Received': officerOpps.filter(
        (o) => o.disclosuresReceivedDate,
      ).length,
      'Submitted to UW': officerOpps.filter((o) => o.submittedToUWDate).length,
      'Approved with Conditions': officerOpps.filter(
        (o) => o.approvedWithConditionsDate,
      ).length,
      'Closing Scheduled': officerOpps.filter((o) => o.closingScheduledDate)
        .length,
      Funded: officerOpps.filter((o) => o.fundedDate).length,
    }

    const submittedOpps = officerOpps.filter((o) => o.submittedToUWDate)
    const cleanFileCount = submittedOpps.filter(
      (o) => !o.isDirtyAtSubmission,
    ).length

    return {
      officer,
      counts,
      conversion: {
        createdToConverted:
          counts['Created'] > 0
            ? (counts['Converted'] / counts['Created']) * 100
            : 0,
        createdToFunded:
          counts['Created'] > 0
            ? (counts['Funded'] / counts['Created']) * 100
            : 0,
      },
      cleanFiles: {
        count: cleanFileCount,
        total: submittedOpps.length,
        percentage:
          submittedOpps.length > 0
            ? (cleanFileCount / submittedOpps.length) * 100
            : 0,
      },
      avgDialsPerClaim:
        officerLeads.length > 0
          ? officerLeads.reduce((sum, l) => sum + l.dialsBeforeClaim, 0) /
            officerLeads.length
          : 0,
      totalCost: officerLeads.reduce((sum, l) => sum + l.costPerLead, 0),
      costPerFunding:
        counts['Funded'] > 0
          ? officerLeads.reduce((sum, l) => sum + l.costPerLead, 0) /
            counts['Funded']
          : 0,
    }
  })
}

export const calculateTeamMetrics = (
  officers: LoanOfficer[],
  officerMetrics: LoanOfficerMetrics[],
): import('../types/throughput').TeamMetrics[] => {
  const teamLeads = officers.filter((o) => o.role === 'Team Lead')

  return teamLeads.map((teamLead) => {
    // Get all officers under this team lead
    const members = officers.filter((o) => o.teamLeadId === teamLead.id)
    const memberIds = new Set(members.map((m) => m.id))

    // Get their metrics
    const memberMetrics = officerMetrics.filter((m) =>
      memberIds.has(m.officer.id),
    )

    // Aggregate counts
    const counts = memberMetrics.reduce(
      (acc, m) => {
        Object.keys(m.counts).forEach((step) => {
          acc[step as import('../types/throughput').FunnelStep] +=
            m.counts[step as import('../types/throughput').FunnelStep]
        })
        return acc
      },
      {
        Created: 0,
        Converted: 0,
        'App Out': 0,
        'App Completed': 0,
        'Disclosures Sent': 0,
        'Disclosures Received': 0,
        'Submitted to UW': 0,
        'Approved with Conditions': 0,
        'Closing Scheduled': 0,
        Funded: 0,
      } as Record<import('../types/throughput').FunnelStep, number>,
    )

    // Aggregate clean files
    const cleanFilesCount = memberMetrics.reduce(
      (sum, m) => sum + m.cleanFiles.count,
      0,
    )
    const cleanFilesTotal = memberMetrics.reduce(
      (sum, m) => sum + m.cleanFiles.total,
      0,
    )

    // Aggregate costs and dials (weighted average by leads/claims)
    const totalCost = memberMetrics.reduce((sum, m) => sum + m.totalCost, 0)

    // For dials, we need to reconstruct total dials since we only have avg
    const totalDials = memberMetrics.reduce(
      (sum, m) => sum + m.avgDialsPerClaim * m.counts['Created'],
      0,
    )

    return {
      teamLead,
      memberMetrics,
      counts,
      conversion: {
        createdToConverted:
          counts['Created'] > 0
            ? (counts['Converted'] / counts['Created']) * 100
            : 0,
        createdToFunded:
          counts['Created'] > 0
            ? (counts['Funded'] / counts['Created']) * 100
            : 0,
      },
      cleanFiles: {
        count: cleanFilesCount,
        total: cleanFilesTotal,
        percentage:
          cleanFilesTotal > 0 ? (cleanFilesCount / cleanFilesTotal) * 100 : 0,
      },
      avgDialsPerClaim:
        counts['Created'] > 0 ? totalDials / counts['Created'] : 0,
      totalCost,
      costPerFunding: counts['Funded'] > 0 ? totalCost / counts['Funded'] : 0,
    }
  })
}
